import { Box, ButtonBase, Skeleton, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { ErrorBoundary } from "react-error-boundary"
import { useNavigate } from "react-router"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useMoney } from "../../../../../currency/useMoney"
import { toDinero } from "../../../../../currency/currencyLib"
import { selectEffectiveUserId } from "../../../../../context/contextSelectors"
import { useAppSelector } from "../../../../../store/storeHooks"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { RefetchIntervals } from "../../../../../../core/api/refetchIntervals"
import { getRefillWalletRoute } from "../../../../../../constants/routes"
import { fetchUserWallets, FetchUserWalletsResponse, WalletsRefill } from "../../../settings/wallets/refill/refillApi"
import { UserWallet } from "../../../settings/wallets/refill/refillTypes"
import { UserWalletsRefillState } from "../../../settings/wallets/refill/pages/UserWalletsRefill"
import { UserDashboardErrorFallbackCard } from "../UserDashboardErrorFallbackCard"
import { CARD_HEIGHT, CARD_WIDTH } from "../dashboardCardDimensions"

const useStyles = makeStyles()((theme) => ({
    sectionTitle: {
        fontSize: "1.25rem",
        fontWeight: 500,
        color: theme.palette.text.primary,
        marginBottom: ".5rem",
    },
    row: {
        display: "flex",
        flexDirection: "row",
        gap: "1rem",
        overflowX: "auto",
    },
    card: {
        flex: "0 0 auto",
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        borderRadius: ".75rem",
        padding: ".5rem 1rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "end",
        textAlign: "left",
        background: theme.palette.tailwind.blue[600],
        color: theme.palette.common.white,
        transition: theme.transitions.create(["background"]),
        "&:hover": {
            background: theme.palette.tailwind.blue[700],
        },
    },
    skeleton: {
        flex: "0 0 auto",
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        borderRadius: ".75rem",
    },
}))

const selectWallets = (data: FetchUserWalletsResponse): UserWallet[] => data.wallets ?? []

export function WalletSummaryCards() {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const navigate = useNavigate()
    const userId = useAppSelector(selectEffectiveUserId) ?? 0

    const { data: wallets, isLoading } = useApiQuery(fetchUserWallets, {
        queryName: WalletsRefill.USER_WALLETS,
        dependencies: { userId },
        enabled: Boolean(userId),
        select: selectWallets,
        refetchInterval: RefetchIntervals.short,
    })

    if (isLoading) {
        return <Skeleton variant="rectangular" className={classes.skeleton} />
    }

    if (!wallets?.length) {
        return null
    }

    const handleOpenRefill = (wallet: UserWallet) => {
        const state: UserWalletsRefillState = { wallet }
        navigate(getRefillWalletRoute(wallet.acquirer.acquirerSetupId, wallet.id), { state })
    }

    return (
        <Box>
            <LocalizedStrict id="user-home-wallets-section-title" vars={{ count: wallets.length }}>
                <Typography component="h3" className={classes.sectionTitle}>
                    Wallets
                </Typography>
            </LocalizedStrict>
            <Box className={classes.row}>
                {wallets.map((wallet) => (
                    <ErrorBoundary key={wallet.id} fallback={<UserDashboardErrorFallbackCard />}>
                        <ButtonBase className={classes.card} onClick={() => handleOpenRefill(wallet)}>
                            <Typography variant="h5" mb=".375rem">
                                {moneyFactory.format(toDinero(wallet.balance))}
                            </Typography>
                            <Typography variant="body1">{wallet.brandingLabel}</Typography>
                        </ButtonBase>
                    </ErrorBoundary>
                ))}
            </Box>
        </Box>
    )
}
